import { Button, BUTTON_TYPE, SIZE } from '@dehaat/dds';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CART_TYPE } from '@/constants/cart';
import { CLIENT_NAME_COOKIE, COOKIE_MAX_AGE_1YEAR, CURRENT_LOCATION_KEY, IS_CREDIT_USER, LAT_LNG_COOKIE_NAME, MANUAL_LOCATION_ENTRY_KEY, REDIRECT_URI_KEY } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import useUIQueryParams from '@/hooks/useUIQueryParams';
import locationIcon from '@/images/location/yourLocation.webp';
import { fetchHyperlocalData, selectCurrentLocation, selectCurrentVendor, selectCurrentVendorId, selectLoadingHyperlocalData, setCurrentLocation, setLoadingHyperlocalData } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { getCurrentLocationValues, getGeoLocationValues } from '@/utils/google';
import { getCookieValue, getLocalStorageKey, isDigiAcreUser, isFromAgcApp, setLocalStorageKey } from '@/utils/helper';
import ButtonLabel, { VARIANT as BUTTONLABEL_VARIANT } from '../ButtonLabel';
import CaretDownIcon from '../icons/CaretDownIcon';
import CurrentLocationIcon from '../icons/CurrentLocationIcon';
import Spinner from '../Spinner';
interface Props {
  mainContainerClassName?: string;
  locationTextClassName?: string;
  allowNavigation?: boolean;
  fromScreen?: string;
  showAddressDetails?: boolean;
}
const CurrentLocation = ({
  mainContainerClassName,
  locationTextClassName,
  allowNavigation = false,
  fromScreen,
  showAddressDetails = true
}: Props) => {
  const {
    t
  } = useTranslation('common');
  const {
    cartType
  } = useContext(CartContext);
  const vendorId = useAppSelector(selectCurrentVendorId);
  const dispatch = useAppDispatch();
  const isHyperlocalDataLoading = useAppSelector(selectLoadingHyperlocalData);
  const currentLocation = useAppSelector(selectCurrentLocation);
  const currentVendor = useAppSelector(selectCurrentVendor);
  const [isLocationLoding, setIsLocationLoding] = useState(false);
  const {
    push,
    asPath,
    replace,
    locale,
    query
  } = useRouter();
  const {
    v
  } = query;
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const uiParams = useUIQueryParams();
  const fromAGC = Boolean(isFromAgcApp(getCookieValue(CLIENT_NAME_COOKIE)));
  const url = fromScreen ? `/location?${REDIRECT_URI_KEY}=${fromScreen}` : '/location';
  const handleLocationChange = () => {
    if (fromScreen) {
      if (fromScreen === '/mandi') trackCustomEvent(CustomEventTypes.MANDI_PRICE_LOCATION_CLICKED);
      push({
        pathname: '/location',
        query: {
          ...uiParams,
          [REDIRECT_URI_KEY]: fromScreen
        }
      });
    } else {
      trackCustomEvent(CustomEventTypes.VENDOR_DROPDOWN_CLICKED);
      if (asPath === '/dcs') {
        push({
          pathname: '/location',
          query: uiParams
        });
      } else if (asPath === '/ondc/dcs') {
        push({
          pathname: '/ondc/location',
          query: uiParams
        });
      } else {
        if (vendorId) {
          push({
            pathname: `${cartType === CART_TYPE.ONDC ? '/ondc' : ''}/dcs`,
            query: uiParams
          });
        } else push({
          pathname: `${cartType === CART_TYPE.ONDC ? '/ondc' : ''}/location`,
          query: uiParams
        });
      }
    }
  };
  const formattedAddress = currentLocation ? currentLocation.formattedAddress : '';
  const handleGetLocation = async () => {
    setIsLocationLoding(true);
    if (!currentLocation && !isHyperlocalDataLoading) {
      const location = await getCurrentLocationValues();
      if (location) {
        trackCustomEvent(CustomEventTypes.LOCATION_PERMISSION_GRANTED);
        const {
          latitude,
          longitude
        } = location;
        const address = await getGeoLocationValues(latitude, longitude);
        dispatch(setCurrentLocation(address));
        setLocalStorageKey(CURRENT_LOCATION_KEY, address);
        document.cookie = `${LAT_LNG_COOKIE_NAME}=${address.latLng}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
        const forCreditOrder = getLocalStorageKey(IS_CREDIT_USER) == '1';
        await dispatch(fetchHyperlocalData({
          latLng: address.latLng,
          language: locale || DEFAULT_LANG,
          vendorId: v as string | undefined,
          isCSCUser: isDigiAcreUser(),
          forCreditOrder
        }));
        replace(asPath);
      } else {
        trackCustomEvent(CustomEventTypes.LOCATION_PERMISSION_DENIED);
        push(url);
      }
    }
    setIsLocationLoding(false);
  };
  const handleEnterLocationManually = () => {
    push({
      pathname: '/location',
      query: {
        ...uiParams,
        [REDIRECT_URI_KEY]: fromScreen || asPath,
        [MANUAL_LOCATION_ENTRY_KEY]: '1'
      }
    });
  };

  // loadingHyperlocalData is by default `true` in redux
  // this helps in showing allow location bottom drawer when users lands on app for the 1st time
  // since we are only sshowing drawer when loadingHyperlocalData is `false`
  useEffect(() => {
    if (!getCookieValue(LAT_LNG_COOKIE_NAME)) dispatch(setLoadingHyperlocalData(false));
  }, [dispatch]);
  const showAccessLocationPopup = !currentLocation && !isHyperlocalDataLoading && typeof window !== 'undefined';
  return <div className={mainContainerClassName} data-sentry-component="CurrentLocation" data-sentry-source-file="CurrentLocation.tsx">
      {showAddressDetails && <div className="flex items-center">
          <CurrentLocationIcon width={24} height={24} className="mr-2" />
          <div>
            <p className="max-w-[170px] md:max-w-[460px] font-nato-semibold text-neutral-100 text-sm line-clamp-3">
              {currentVendor && !fromAGC ? currentVendor?.company_name : t('your_location')}
            </p>
            {currentLocation && <button aria-label="Click here to change location or vendor" disabled={!allowNavigation} onClick={() => !fromAGC ? handleLocationChange() : null} className={`flex py-1 items-center ${allowNavigation ? 'cursor-pointer' : ''}`}>
                <ButtonLabel variant={BUTTONLABEL_VARIANT.TWO} className={`font-medium text-xs text-neutral-90 max-w-[150px] md:max-w-[360px] truncate ${locationTextClassName}`} label={formattedAddress} />
                {allowNavigation && !fromAGC && <CaretDownIcon className="fill-transparent stroke-neutral-90 w-5 h-5" />}
              </button>}
          </div>
        </div>}
      {isLocationLoding && typeof window !== 'undefined' && createPortal(<div className="fixed inset-0 z-[99] flex items-end">
            {isLocationLoding && !showAccessLocationPopup && <Spinner />}
          </div>, window.document.body)}

      {showAccessLocationPopup && createPortal(<section data-testid="ask-location-popup" className="fixed inset-0 z-[99] flex items-end" style={{
      backgroundColor: 'rgba(0,0,0,0.5)'
    }}>
            {isLocationLoding && <Spinner />}
            <div className="w-full bg-white px-4 py-6 flex flex-col items-center gap-3">
              <Image height={96} width={96} src={locationIcon} alt="" />
              <h5 className="text-neutral-90">
                {t('location:access_to_location')}
              </h5>
              <p className="font-medium text-neutral-90 text-center">
                {t('location:location_for_dehaat_center')}
              </p>
              <Button text={t('location:allow_location')} onClick={handleGetLocation} size={SIZE.large} buttonStyle={{
          marginTop: 8,
          width: '100%'
        }} />
              <Button text={t('location:enter_location_manually')} onClick={handleEnterLocationManually} size={SIZE.large} type={BUTTON_TYPE.SECONDARY} buttonStyle={{
          width: '100%'
        }} />
            </div>
          </section>, document.body)}
    </div>;
};
export default CurrentLocation;